import React, { useEffect, useState } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Mail,
  Send,
  Loader2,
  Eye,
  Users,
  RefreshCcw,
  Plus,
  Trash2,
  ArrowUp,
  ArrowDown,
  Pencil,
  Clock,
  CalendarClock,
  CheckCircle2,
  ListOrdered,
  Sparkles,
} from 'lucide-react';
import { toast } from 'sonner';
import { api } from './api';
import { logger } from '../lib/logger';

const TEMPLATE_HTML = `<p>Coach Lewis here.</p>

<p>This week's mental rep: <strong>Run The 3-Breath Reset before every practice.</strong></p>

<p>Three breaths. Long exhale to drop tension, a box breath to anchor, a power inhale + cue word to launch. 90 seconds, max.</p>

<p>What was your win this week? Reply and tell me.</p>

<p>— Coach Lewis</p>`;

const DAY_NAMES = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const HOURS = Array.from({ length: 24 }, (_, i) => i);

function fmtTime12(hour, minute) {
  const ampm = hour >= 12 ? 'pm' : 'am';
  const h12 = ((hour + 11) % 12) + 1;
  return `${h12}:${String(minute).padStart(2, '0')} ${ampm}`;
}

function fmtScheduleSummary(s) {
  if (!s) return '—';
  if (!s.enabled) return 'Auto-send is OFF';
  const time = fmtTime12(s.hour ?? 8, s.minute ?? 0);
  if (s.cadence === 'monthly') return `Monthly on day ${s.day_of_month} at ${time}`;
  const day = DAY_NAMES[s.day_of_week ?? 1] || 'Tuesday';
  const cad = s.cadence === 'biweekly' ? 'Every other ' : 'Every ';
  return `${cad}${day} at ${time} ${s.timezone || 'America/New_York'}`;
}

function fmtNextRun(iso, tz) {
  if (!iso) return '—';
  const d = new Date(iso);
  try {
    return d.toLocaleString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      timeZone: tz || 'America/New_York',
      timeZoneName: 'short',
    });
  } catch {
    return d.toLocaleString();
  }
}

const STATUS_BADGE = {
  queued: 'bg-[#E6F4F6] text-[#003a40] border-[#004C54]/20',
  sent: 'bg-emerald-50 text-emerald-800 border-emerald-200',
  sending: 'bg-amber-50 text-amber-800 border-amber-200',
  failed: 'bg-red-50 text-red-800 border-red-200',
  cancelled: 'bg-slate-50 text-slate-600 border-slate-200',
};

export default function AdminNewsletter() {
  // Composer state
  const [subject, setSubject] = useState("This week's mental rep");
  const [html, setHtml] = useState(TEMPLATE_HTML);
  const [editingId, setEditingId] = useState(null);
  const [busyCompose, setBusyCompose] = useState(false);
  const [recipientCount, setRecipientCount] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [confirmSendNow, setConfirmSendNow] = useState(false);
  const [confirmSeed, setConfirmSeed] = useState(false);
  const [seeding, setSeeding] = useState(false);

  // Queue + schedule + audience state
  const [queue, setQueue] = useState(null);
  const [schedule, setSchedule] = useState(null);
  const [activeSubs, setActiveSubs] = useState(null);
  const [history, setHistory] = useState([]);

  const loadAll = async () => {
    try {
      const [q, s, subs, h] = await Promise.all([
        api.get('/api/admin/newsletter/queue'),
        api.get('/api/admin/newsletter/schedule'),
        api.get('/api/admin/subscribers'),
        api.get('/api/admin/newsletter/sends'),
      ]);
      setQueue(q.items || []);
      setSchedule(s);
      setActiveSubs(typeof subs.active === 'number' ? subs.active : (subs.count || 0));
      setHistory(h.sends || []);
    } catch (e) {
      logger.error('admin newsletter load failed', e);
      toast.error(e.message || 'Could not load newsletter data.');
    }
  };

  // Bootstraps queue / schedule / subs / history on mount. The setState
  // calls in loadAll() are the legitimate outputs of this fetch-and-set
  // effect; suppress set-state-in-effect + exhaustive-deps accordingly.
  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => { loadAll(); }, []);
  /* eslint-enable react-hooks/exhaustive-deps */

  const resetCompose = () => {
    setSubject("This week's mental rep");
    setHtml(TEMPLATE_HTML);
    setEditingId(null);
    setRecipientCount(null);
  };

  const queueItems = (queue || []).filter((q) => q.status === 'queued')
    .sort((a, b) => a.order - b.order);
  const sentItems = (queue || []).filter((q) => q.status === 'sent')
    .sort((a, b) => new Date(b.sent_at || 0) - new Date(a.sent_at || 0));

  const saveDraft = async () => {
    if (!subject.trim() || html.length < 10) {
      toast.error('Subject and body are required.');
      return;
    }
    setBusyCompose(true);
    try {
      if (editingId) {
        await api.patch(`/api/admin/newsletter/queue/${editingId}`, { subject, html_body: html });
        toast.success('Queue item updated.');
      } else {
        await api.post('/api/admin/newsletter/queue', { subject, html_body: html });
        toast.success('Added to the queue.');
      }
      resetCompose();
      await loadAll();
    } catch (e) {
      toast.error(e.message || 'Could not save.');
    } finally {
      setBusyCompose(false);
    }
  };

  const checkRecipients = async () => {
    setPreviewing(true);
    try {
      const res = await api.post('/api/admin/newsletter/send', { subject, html_body: html, dry_run: true });
      setRecipientCount(res.would_send_to);
    } catch (e) {
      toast.error(e.message || 'Could not check recipients.');
    } finally {
      setPreviewing(false);
    }
  };

  const sendNowDirect = async () => {
    setConfirmSendNow(false);
    setBusyCompose(true);
    try {
      const res = await api.post('/api/admin/newsletter/send', { subject, html_body: html, dry_run: false });
      toast.success(`Sent ${res.sent} of ${res.total}.${res.failed ? ` ${res.failed} failed.` : ''}`);
      await loadAll();
    } catch (e) {
      toast.error(e.message || 'Send failed.');
    } finally {
      setBusyCompose(false);
    }
  };

  const editItem = (item) => {
    setEditingId(item.id);
    setSubject(item.subject);
    setHtml(item.html_body);
    setRecipientCount(null);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const deleteItem = async (item) => {
    if (!window.confirm(`Delete "${item.subject}" from the queue?`)) return;
    try {
      await api.del(`/api/admin/newsletter/queue/${item.id}`);
      toast.success('Removed.');
      await loadAll();
    } catch (e) {
      toast.error(e.message || 'Could not delete.');
    }
  };

  // Swap order of two adjacent queued items.
  const reorderSwap = async (a, b) => {
    try {
      await api.patch(`/api/admin/newsletter/queue/${a.id}`, { order: b.order });
      await api.patch(`/api/admin/newsletter/queue/${b.id}`, { order: a.order });
      await loadAll();
    } catch (e) {
      toast.error(e.message || 'Could not reorder.');
    }
  };

  const sendQueuedNow = async (item) => {
    if (!window.confirm(`Send "${item.subject}" now to all active subscribers? This bypasses the schedule.`)) return;
    try {
      const res = await api.post(`/api/admin/newsletter/queue/${item.id}/send-now`, {});
      toast.success(`Sent: ${res.result?.sent ?? 0} of ${res.result?.total ?? 0}`);
      await loadAll();
    } catch (e) {
      toast.error(e.message || 'Send failed.');
    }
  };

  // Schedule updaters
  const patchSchedule = async (patch) => {
    try {
      const updated = await api.put('/api/admin/newsletter/schedule', patch);
      setSchedule((s) => ({ ...(s || {}), ...updated }));
      toast.success('Schedule updated.');
    } catch (e) {
      toast.error(e.message || 'Could not update schedule.');
    }
  };

  // Seed the canonical batch of pre-drafted newsletters (idempotent).
  const seedDefaultDrafts = async () => {
    setConfirmSeed(false);
    setSeeding(true);
    try {
      const res = await api.post('/api/admin/newsletter/queue/seed-defaults', {});
      const inserted = res?.inserted_count ?? 0;
      const skipped = res?.skipped_count ?? 0;
      if (inserted === 0 && skipped > 0) {
        toast.success(`All ${skipped} default drafts are already in the queue.`);
      } else if (inserted > 0 && skipped === 0) {
        toast.success(`Added ${inserted} default draft${inserted === 1 ? '' : 's'} to the queue.`);
      } else {
        toast.success(`Added ${inserted}, skipped ${skipped} that were already queued.`);
      }
      await loadAll();
    } catch (e) {
      toast.error(e.message || 'Could not seed default drafts.');
    } finally {
      setSeeding(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-newsletter">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Mail className="h-5 w-5 text-[#004C54]" />
          <h1 className="font-heading text-2xl font-bold">Newsletter</h1>
        </div>
        <div
          className="inline-flex items-center gap-2 rounded-full bg-[#E6F4F6] border border-[#004C54]/15 px-3.5 py-1.5"
          data-testid="newsletter-audience-badge"
        >
          <Users className="h-4 w-4 text-[#004C54]" />
          <span className="text-[13px] font-semibold text-[#003a40]">
            {activeSubs === null ? 'Loading…' : `${activeSubs} active subscriber${activeSubs === 1 ? '' : 's'}`}
          </span>
          <button type="button" onClick={loadAll} className="ml-1 text-[#004C54]/70 hover:text-[#004C54]" aria-label="Refresh" data-testid="newsletter-refresh">
            <RefreshCcw className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>

      {/* Schedule card */}
      <Card data-testid="schedule-card">
        <CardContent className="p-5 space-y-4">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-2">
              <CalendarClock className="h-4 w-4 text-[#004C54]" />
              <h2 className="font-heading font-bold text-[17px]">Auto-send schedule</h2>
              {schedule?.enabled ? (
                <Badge className="bg-emerald-50 text-emerald-800 border-emerald-200">ON</Badge>
              ) : (
                <Badge variant="outline" className="border-slate-300 text-slate-600">OFF</Badge>
              )}
            </div>
            <label className="flex items-center gap-2 text-[13px] font-medium text-foreground/80">
              <Switch
                checked={!!schedule?.enabled}
                onCheckedChange={(v) => patchSchedule({ enabled: v })}
                data-testid="schedule-enabled-toggle"
              />
              {schedule?.enabled ? 'Auto-send is ON' : 'Auto-send is OFF'}
            </label>
          </div>

          {/* Summary line + next run */}
          <div className="rounded-lg border border-[#004C54]/15 bg-[#F0F8F9] px-4 py-3 space-y-1">
            <div className="text-[13.5px] font-semibold text-[#003a40]" data-testid="schedule-summary">
              {fmtScheduleSummary(schedule)}
            </div>
            <div className="text-[12.5px] text-foreground/70 flex flex-wrap gap-x-3 gap-y-1">
              <span className="inline-flex items-center gap-1">
                <Clock className="h-3.5 w-3.5" />
                Next send: <span className="font-medium text-foreground">{fmtNextRun(schedule?.next_run_at, schedule?.timezone)}</span>
              </span>
              {schedule?.next_item ? (
                <span>
                  → <span className="font-medium text-foreground">{schedule.next_item.subject}</span>
                </span>
              ) : (
                <span className="italic text-foreground/60">→ Queue is empty (no email will go out)</span>
              )}
            </div>
          </div>

          {/* Schedule controls */}
          <div className="grid sm:grid-cols-4 gap-3">
            <div>
              <Label className="text-[11px] uppercase tracking-wider text-foreground/60">Cadence</Label>
              <Select value={schedule?.cadence || 'weekly'} onValueChange={(v) => patchSchedule({ cadence: v })}>
                <SelectTrigger className="mt-1" data-testid="schedule-cadence"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="weekly">Weekly</SelectItem>
                  <SelectItem value="biweekly">Every other week</SelectItem>
                  <SelectItem value="monthly">Monthly</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {schedule?.cadence === 'monthly' ? (
              <div>
                <Label className="text-[11px] uppercase tracking-wider text-foreground/60">Day of month</Label>
                <Select
                  value={String(schedule?.day_of_month ?? 1)}
                  onValueChange={(v) => patchSchedule({ day_of_month: Number(v) })}
                >
                  <SelectTrigger className="mt-1" data-testid="schedule-day-of-month"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Array.from({ length: 28 }, (_, i) => i + 1).map((d) => (
                      <SelectItem key={d} value={String(d)}>{d}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ) : (
              <div>
                <Label className="text-[11px] uppercase tracking-wider text-foreground/60">Day of week</Label>
                <Select
                  value={String(schedule?.day_of_week ?? 1)}
                  onValueChange={(v) => patchSchedule({ day_of_week: Number(v) })}
                >
                  <SelectTrigger className="mt-1" data-testid="schedule-day-of-week"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {DAY_NAMES.map((d, i) => (
                      <SelectItem key={i} value={String(i)}>{d}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div>
              <Label className="text-[11px] uppercase tracking-wider text-foreground/60">Hour</Label>
              <Select
                value={String(schedule?.hour ?? 8)}
                onValueChange={(v) => patchSchedule({ hour: Number(v) })}
              >
                <SelectTrigger className="mt-1" data-testid="schedule-hour"><SelectValue /></SelectTrigger>
                <SelectContent className="max-h-60">
                  {HOURS.map((h) => (
                    <SelectItem key={h} value={String(h)}>{fmtTime12(h, 0)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-[11px] uppercase tracking-wider text-foreground/60">Timezone</Label>
              <Select
                value={schedule?.timezone || 'America/New_York'}
                onValueChange={(v) => patchSchedule({ timezone: v })}
              >
                <SelectTrigger className="mt-1" data-testid="schedule-timezone"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="America/New_York">Eastern (NY)</SelectItem>
                  <SelectItem value="America/Chicago">Central</SelectItem>
                  <SelectItem value="America/Denver">Mountain</SelectItem>
                  <SelectItem value="America/Los_Angeles">Pacific</SelectItem>
                  <SelectItem value="UTC">UTC</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <p className="text-[11.5px] text-foreground/55">
            When auto-send is ON, the queue&rsquo;s top item is sent on the next scheduled date. If the queue is empty at that moment, nothing goes out and the schedule rolls forward.
          </p>
        </CardContent>
      </Card>

      {/* Composer */}
      <Card data-testid="composer-card">
        <CardContent className="p-5 space-y-4">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-2">
              <Pencil className="h-4 w-4 text-[#004C54]" />
              <h2 className="font-heading font-bold text-[17px]">
                {editingId ? 'Edit queued newsletter' : 'Compose a newsletter'}
              </h2>
            </div>
            {editingId && (
              <Button variant="ghost" size="sm" onClick={resetCompose} data-testid="composer-cancel-edit">
                Cancel edit
              </Button>
            )}
          </div>

          <div>
            <Label className="text-[11px] uppercase tracking-wider text-foreground/60">Subject</Label>
            <Input
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="This week's mental rep"
              className="mt-1"
              data-testid="newsletter-subject"
            />
          </div>

          <div>
            <Label className="text-[11px] uppercase tracking-wider text-foreground/60">Body (HTML)</Label>
            <Textarea
              value={html}
              onChange={(e) => setHtml(e.target.value)}
              rows={12}
              className="mt-1 font-mono text-xs"
              data-testid="newsletter-body"
            />
            <p className="text-[11.5px] text-foreground/50 mt-1">
              &lt;p&gt;, &lt;strong&gt;, and &lt;a&gt; tags work. Keep it short — 200-400 words is ideal.
            </p>
          </div>

          {recipientCount !== null && (
            <div
              className="rounded-lg border border-[#004C54]/15 bg-[#F0F8F9] px-4 py-2.5 text-[13px] font-medium text-[#003a40]"
              data-testid="composer-recipient-summary"
            >
              <CheckCircle2 className="inline h-4 w-4 mr-1.5 text-[#004C54]" />
              {recipientCount} subscriber{recipientCount === 1 ? '' : 's'} will receive this when sent.
            </div>
          )}

          <div className="flex flex-wrap gap-2 pt-2 border-t border-border">
            <Button
              onClick={saveDraft}
              disabled={busyCompose || !subject.trim() || html.length < 10}
              className="bg-[#004C54] hover:bg-[#003a40] text-white"
              data-testid="composer-add-to-queue"
            >
              {busyCompose ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : (editingId ? <CheckCircle2 className="h-4 w-4 mr-1.5" /> : <Plus className="h-4 w-4 mr-1.5" />)}
              {editingId ? 'Save changes' : 'Add to queue'}
            </Button>
            <Button variant="outline" onClick={checkRecipients} disabled={previewing} data-testid="composer-check-recipients">
              {previewing ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Eye className="h-4 w-4 mr-1.5" />}
              Check recipient count
            </Button>
            <Button
              variant="outline"
              onClick={() => setConfirmSendNow(true)}
              disabled={busyCompose || !subject.trim() || html.length < 10}
              data-testid="composer-send-now"
            >
              <Send className="h-4 w-4 mr-1.5" />
              Send now (bypass queue)
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Queue */}
      <Card data-testid="queue-card">
        <CardContent className="p-5">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
            <div className="flex items-center gap-2">
              <ListOrdered className="h-4 w-4 text-[#004C54]" />
              <h2 className="font-heading font-bold text-[17px]">Queue</h2>
              <Badge variant="outline" className="border-[#004C54]/25 text-[#003a40]">
                {queueItems.length} waiting
              </Badge>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setConfirmSeed(true)}
              disabled={seeding}
              className="text-[#004C54] border-[#004C54]/25 hover:bg-[#E6F4F6]"
              data-testid="queue-seed-defaults"
            >
              {seeding ? (
                <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
              ) : (
                <Sparkles className="h-4 w-4 mr-1.5" />
              )}
              Seed default drafts
            </Button>
          </div>

          {queue === null ? (
            <div className="text-sm text-foreground/55">Loading…</div>
          ) : queueItems.length === 0 ? (
            <div
              className="rounded-lg border border-dashed border-border bg-[#FAFBFB] py-8 px-5 text-center text-foreground/65 text-[13.5px]"
              data-testid="queue-empty"
            >
              <p className="mb-3">
                Queue is empty. Compose a newsletter above, or one-click seed the canonical batch of 8 pre-drafted newsletters Coach Lewis prepared.
              </p>
              <Button
                onClick={() => setConfirmSeed(true)}
                disabled={seeding}
                className="bg-[#004C54] hover:bg-[#003a40] text-white"
                data-testid="queue-empty-seed"
              >
                {seeding ? (
                  <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
                ) : (
                  <Sparkles className="h-4 w-4 mr-1.5" />
                )}
                Seed 8 default drafts
              </Button>
              {schedule?.enabled && (
                <div className="mt-3 text-[12px] text-amber-700">
                  Auto-send is ON but nothing will go out until the queue has items.
                </div>
              )}
            </div>
          ) : (
            <ul className="space-y-2" data-testid="queue-list">
              {queueItems.map((item, idx) => (
                <li
                  key={item.id}
                  className="rounded-lg border border-border bg-white p-3.5 flex flex-wrap items-start gap-3"
                  data-testid={`queue-item-${item.id}`}
                >
                  <div
                    className="w-9 h-9 rounded-lg flex items-center justify-center bg-[#E6F4F6] text-[#004C54] font-bold text-[15px] shrink-0"
                    aria-hidden="true"
                  >
                    {idx + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-[14.5px] text-[#003a40] truncate">{item.subject}</div>
                    <div className="text-[12px] text-foreground/55 mt-0.5">
                      Added {item.created_at ? new Date(item.created_at).toLocaleDateString() : ''}
                      {idx === 0 && schedule?.enabled && schedule?.next_run_at && (
                        <span className="ml-2 text-[#004C54] font-medium">
                          · Next to send {fmtNextRun(schedule.next_run_at, schedule.timezone)}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <Button
                      type="button" variant="ghost" size="icon"
                      disabled={idx === 0}
                      onClick={() => reorderSwap(item, queueItems[idx - 1])}
                      aria-label="Move up"
                      data-testid={`queue-up-${item.id}`}
                    >
                      <ArrowUp className="h-4 w-4" />
                    </Button>
                    <Button
                      type="button" variant="ghost" size="icon"
                      disabled={idx === queueItems.length - 1}
                      onClick={() => reorderSwap(item, queueItems[idx + 1])}
                      aria-label="Move down"
                      data-testid={`queue-down-${item.id}`}
                    >
                      <ArrowDown className="h-4 w-4" />
                    </Button>
                    <Button
                      type="button" variant="ghost" size="icon"
                      onClick={() => editItem(item)}
                      aria-label="Edit"
                      data-testid={`queue-edit-${item.id}`}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      type="button" variant="ghost" size="sm"
                      onClick={() => sendQueuedNow(item)}
                      data-testid={`queue-send-now-${item.id}`}
                      className="text-[#004C54]"
                    >
                      <Send className="h-3.5 w-3.5 mr-1" /> Send now
                    </Button>
                    <Button
                      type="button" variant="ghost" size="icon"
                      onClick={() => deleteItem(item)}
                      aria-label="Delete"
                      data-testid={`queue-delete-${item.id}`}
                    >
                      <Trash2 className="h-4 w-4 text-red-600" />
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      {/* Recently sent */}
      {(sentItems.length > 0 || history.length > 0) && (
        <Card data-testid="recent-sends-card">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="font-semibold flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4 text-emerald-700" /> Recent sends
              </div>
              <span className="text-[11.5px] text-foreground/50">{history.length} total</span>
            </div>
            {history.length === 0 ? (
              <div className="text-sm text-foreground/60 italic">No sends yet.</div>
            ) : (
              <div className="divide-y divide-border">
                {history.map((h) => (
                  <div key={h.send_id} className="py-2.5 flex flex-wrap items-center justify-between gap-2 text-sm">
                    <div className="min-w-0">
                      <div className="font-semibold truncate">{h.subject}</div>
                      <div className="text-xs text-foreground/60">{new Date(h.started_at).toLocaleString()}</div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className={STATUS_BADGE.sent}>{h.sent ?? '?'} sent</Badge>
                      {(h.failed ?? 0) > 0 && (
                        <Badge variant="outline" className={STATUS_BADGE.failed}>{h.failed} failed</Badge>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Send-now confirmation */}
      <AlertDialog open={confirmSendNow} onOpenChange={setConfirmSendNow}>
        <AlertDialogContent data-testid="confirm-send-now-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Send this newsletter now?</AlertDialogTitle>
            <AlertDialogDescription>
              <span className="block mb-2">
                <span className="font-semibold text-foreground">{subject || '(no subject)'}</span>
                {' '}will be delivered to{' '}
                <span className="font-semibold text-foreground">
                  {(recipientCount ?? activeSubs) ?? '?'} active subscriber{(recipientCount ?? activeSubs) === 1 ? '' : 's'}
                </span>.
              </span>
              <span className="block text-[12.5px] text-foreground/60">
                This bypasses the queue. The send cannot be undone.
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={sendNowDirect} className="bg-[#004C54] hover:bg-[#003a40] text-white">
              <Send className="h-4 w-4 mr-1.5" />
              Send now
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Seed-defaults confirmation */}
      <AlertDialog open={confirmSeed} onOpenChange={setConfirmSeed}>
        <AlertDialogContent data-testid="confirm-seed-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Seed default drafts?</AlertDialogTitle>
            <AlertDialogDescription>
              <span className="block mb-2">
                Adds Coach Lewis&rsquo;s 8 pre-drafted newsletters (3-Breath Reset, ABC Stack, the 24-second Mistake Rule, Car-Ride-Home, process goals, self-talk, pre-game warm-up, and the Apex Six summary) to the queue.
              </span>
              <span className="block text-[12.5px] text-foreground/60">
                Idempotent &mdash; any draft already queued under the same subject is skipped, so this is safe to run more than once. Auto-send will be turned on if it&rsquo;s currently off.
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="confirm-seed-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={seedDefaultDrafts}
              className="bg-[#004C54] hover:bg-[#003a40] text-white"
              data-testid="confirm-seed-action"
            >
              <Sparkles className="h-4 w-4 mr-1.5" />
              Seed drafts
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
